import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Spinner } from "@telegram-apps/telegram-ui";
import { ensureSession, getSession, onSessionChange } from "../lib/api.js";
import { isInTelegram } from "../lib/telegram.js";

// Gates the entire app behind a valid session.
//
// - In a regular browser: if no stored session, redirect to /login so the
//   user can click the Telegram Login Widget.
// - Inside a Telegram Mini App: silently bootstrap via initData. The user
//   never sees /login in that case.
//
// Re-renders when ensureSession() resolves or signOut() fires.
export default function AuthGate({ children }) {
  const [state, setState] = useState(() => {
    const s = getSession();
    return s ? { status: "ready", session: s } : { status: "loading" };
  });
  const location = useLocation();

  useEffect(() => {
    const off = onSessionChange((s) => {
      if (s) setState({ status: "ready", session: s });
      else setState({ status: "unauthenticated" });
    });
    return off;
  }, []);

  useEffect(() => {
    if (state.status !== "loading") return;
    let cancelled = false;
    ensureSession()
      .then((s) => { if (!cancelled) setState({ status: "ready", session: s }); })
      .catch((e) => {
        if (cancelled) return;
        if (e?.message === "not_authenticated" || e?.message === "not_in_telegram") {
          setState({ status: "unauthenticated" });
        } else {
          setState({ status: "error", error: e });
        }
      });
    return () => { cancelled = true; };
  }, [state.status]);

  if (state.status === "loading") {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
        <Spinner size="l" />
      </div>
    );
  }

  if (state.status === "unauthenticated") {
    if (isInTelegram()) {
      return <div style={{ padding: 16 }}>Не удалось получить данные Telegram. Перезапустите Mini App.</div>;
    }
    // Root visitors land on the marketing/landing page first; anyone deeplinking
    // to a specific app path (e.g. /book) is sent straight to the login widget.
    const target = location.pathname === "/" ? "/welcome" : "/login";
    return <Navigate to={target} replace state={{ from: location.pathname }} />;
  }

  if (state.status === "error") {
    return (
      <div style={{ padding: 16 }}>
        Ошибка авторизации: {state.error?.message || "unknown"}
      </div>
    );
  }

  return children;
}
